import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowRightIcon, CheckIcon, Loader2Icon } from 'lucide-react';
import { GoldMedal } from './LifetimeSeal';
import { money } from './QuantityStepper';
import type { UpsellStep } from '../data/upsellFlows';
interface UpsellModalProps {
  step: UpsellStep | null;
  /** True while the customer is being routed to the Shopify cart. */
  routing: boolean;
  onAccept: () => void;
  onDecline: () => void;
  /** Close without choosing: stays on the page, nothing is added. */
  onClose: () => void;
}
const ease = [0.23, 1, 0.32, 1] as const;

/**
 * One negotiation step. Always shows what the customer has now, what the offer
 * is, the exact extra cost and the new total, with a visible way to say no.
 */
export function UpsellModal({
  step,
  routing,
  onAccept,
  onDecline,
  onClose
}: UpsellModalProps) {
  const acceptRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!step) return;
    acceptRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !routing) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, routing, onClose]);
  return <AnimatePresence data-id="element-923" data-mp="dkltcsziznea">
      {step && <motion.div className="fixed inset-0 z-50 flex items-end justify-center bg-ink-950/85 backdrop-blur-sm sm:items-center sm:p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.16,
      ease
    }} role="dialog" aria-modal="true" aria-labelledby="upsell-heading" data-id="element-924" data-mp="djkjgru8x6y5">
          <motion.div key={step.id} className="relative max-h-[92vh] w-full max-w-xl overflow-y-auto border-2 gold-edge bg-ink-950 shadow-[0_0_48px_rgba(242,201,76,0.14)]" initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 8
      }} transition={{
        duration: 0.22,
        ease
      }} data-id="element-925" data-mp="3qs717kku78mj">
            <div className="gold-plate flex items-center justify-between gap-3 px-5 py-2.5" data-id="element-926" data-mp="2m81rrgribn05">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-ink-950" data-id="element-927" data-mp="2zzgfg0wd0cdj">{step.plate}</p>
              <button type="button" onClick={onClose} disabled={routing} aria-label="Close" className="-mr-2 flex h-8 w-8 items-center justify-center text-ink-950 transition-opacity duration-150 ease-grit hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-950" data-id="element-928" data-mp="2v1n64yzrf291">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-929" data-mp="2c8stwctito8i" />
              </button>
            </div>

            <div className="p-5 sm:p-7" data-id="element-930" data-mp="3smsp5ahf8ppf">
              <h2 id="upsell-heading" className="grit-headline text-[clamp(1.85rem,6.5vw,3rem)] font-extrabold uppercase leading-[0.95] text-offwhite" data-id="element-931" data-mp="3npst7sead3c1">
                {step.headline}
                <br data-id="element-932" data-mp="1ehxfw3lhhplc" />
                <span className="gold-text" data-id="element-933" data-mp="1ei5wa5gtfd1a">{step.highlight}</span>
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-steel-200" data-id="element-934" data-mp="10iel0chooaqz">{step.intro}</p>

              {/* Current vs. new, side by side even on phones: totals are the first thing read. */}
              <div className="mt-5 grid grid-cols-2 gap-2 sm:gap-3" data-id="element-935" data-mp="1t722dwzbafz9">
                <div className="border border-white/12 bg-white/[0.03] p-3 sm:p-4" data-id="element-936" data-mp="1x7vpwp8udsrm">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-937" data-mp="1jfz4f41oih47">Your selection</p>
                  <p className="mt-1.5 font-display text-base font-bold uppercase leading-tight text-offwhite sm:text-lg" data-id="element-938" data-mp="1oeim2s98p6qr">
                    {step.current.label}
                  </p>
                  <p className="mt-0.5 text-[12px] leading-snug text-steel-400" data-id="element-939" data-mp="2gv35urhmv67t">{step.current.detail}</p>
                  <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-940" data-mp="s9a58xg9rvyb">Current total</p>
                  <p className="font-display text-3xl font-extrabold leading-none text-offwhite" data-id="element-941" data-mp="n9e373r4uele">
                    {money(step.current.total)}
                  </p>
                </div>
                <div className="border-2 border-grit-500 bg-grit-500/[0.07] p-3 sm:p-4" data-id="element-942" data-mp="eaiwzro394i3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-grit-500" data-id="element-943" data-mp="1b0jhvflc70u4">Upgrade</p>
                  <p className="mt-1.5 font-display text-base font-bold uppercase leading-tight text-offwhite sm:text-lg" data-id="element-944" data-mp="16y6dauibt8yx">
                    {step.upgrade.label}
                  </p>
                  <p className="mt-0.5 text-[12px] leading-snug text-steel-300" data-id="element-945" data-mp="x8v4l8zcq6bk">{step.upgrade.detail}</p>
                  <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.18em] text-grit-500" data-id="element-946" data-mp="1uoiukvzgoi26">New total</p>
                  <p className="font-display text-3xl font-extrabold leading-none text-grit-500" data-id="element-947" data-mp="1px0vkdh00rju">
                    {money(step.upgrade.total)}
                  </p>
                </div>
              </div>
              <p className="mt-2 bg-grit-500 px-4 py-2 text-center font-display text-xl font-extrabold uppercase tracking-wide text-ink-950" data-id="element-948" data-mp="228l59ao5qe1z">
                {step.extra}
              </p>

              {step.valueLine && <dl className="mt-4 grid grid-cols-3 border border-white/10 text-center" data-id="element-949" data-mp="2b9j99sylilkk">
                  <div className="p-2.5" data-id="element-950" data-mp="2g6rpho06ofj6">
                    <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-steel-400" data-id="element-951" data-mp="2l64dic2sjsnc">Normal value</dt>
                    <dd className="mt-0.5 font-display text-lg font-bold text-steel-400 line-through" data-id="element-952" data-mp="2pwuiho7b1thi">
                      {step.valueLine.normal}
                    </dd>
                  </div>
                  <div className="border-x border-white/10 p-2.5" data-id="element-953" data-mp="2yyarpsmdvpk5">
                    <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-grit-500" data-id="element-954" data-mp="2yvy5v4di8v1t">Your upgrade</dt>
                    <dd className="mt-0.5 font-display text-lg font-bold text-grit-500" data-id="element-955" data-mp="13ucr6eltzviw">{step.valueLine.today}</dd>
                  </div>
                  <div className="p-2.5" data-id="element-956" data-mp="zuc1tyx71zx2">
                    <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-steel-400" data-id="element-957" data-mp="zr7uisofmo26">You save</dt>
                    <dd className="mt-0.5 font-display text-lg font-bold text-offwhite" data-id="element-958" data-mp="18tz56q9hhfsr">
                      {step.valueLine.save.replace(/^Save /, '')}
                    </dd>
                  </div>
                </dl>}

              <div className="mt-4 flex items-start gap-4" data-id="element-959" data-mp="1iiqv6suzhq11">
                {step.lifetimeMedal && <div className="hidden shrink-0 sm:block" data-id="element-960" data-mp="1wi073gc5pxz7">
                    <GoldMedal size={78} data-id="element-961" data-mp="1rjp0rfl2m5lq" />
                  </div>}
                <div data-id="element-962" data-mp="25ahvbwqhcy5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-963" data-mp="24h4vt76lr7k">You get</p>
                  <ul className="mt-2 grid gap-1.5 text-[14px] font-semibold text-steel-200" data-id="element-964" data-mp="3tcmy9opslmd0">
                    {step.includes.map(line => <li key={line} className="flex items-start gap-2" data-id="element-965" data-mp="28vm2nrdixm3p">
                        <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-966" data-mp="2ds486tgohoh3" />
                        {line}
                      </li>)}
                  </ul>
                </div>
              </div>

              {step.clarification && <p className="mt-4 border-l-2 border-grit-500 pl-3 text-[13px] font-bold uppercase tracking-wide text-offwhite" data-id="element-967" data-mp="1q29wif83i5yf">
                  {step.clarification}
                </p>}

              <button ref={acceptRef} type="button" onClick={onAccept} disabled={routing} className="mt-5 flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 text-center font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite sm:text-2xl" data-id="element-968" data-mp="1h3ocsgqw0kw2">
                {routing ? <>
                    <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-969" data-mp="1th03pi94h0y5" />
                    Taking you to checkout
                  </> : <>
                    {step.accept}
                    <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-970" data-mp="1fj1pccusx4in" />
                  </>}
              </button>
              <button type="button" onClick={onDecline} disabled={routing} className="mt-2.5 w-full border border-white/20 px-4 py-3 text-center text-[13px] font-bold uppercase tracking-wide text-steel-300 transition-colors duration-150 ease-grit hover:border-white/40 hover:text-offwhite disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-971" data-mp="1al04bmdtev4j">
                {step.decline}
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}